import { em, math } from 'polished'
import styled, { css } from 'styled-components'
import faqToggleMinus from './assets/faq-toggle-minus.svg'
import faqTogglePlus from './assets/faq-toggle-plus.svg'
import promoArrowRight from './assets/promo-arrow-right.svg'
import recommendationCounterMinus from './assets/recommendation-counter-minus.svg'
import recommendationCounterPlus from './assets/recommendation-counter-plus.svg'
import summaryCounterMinus from './assets/summary-counter-minus.svg'
import summaryCounterPlus from './assets/summary-counter-plus.svg'

const CART_ACCENT_PURPLE = '#7645EF'

export const PageTop = styled.div`
  padding-top: ${p => math(`${p.theme.blocks.menuHeight} + ${em(39)}`)};
  padding-left: ${p => p.theme.indents.baseIndentXs};
  padding-right: ${p => p.theme.indents.baseIndentXs};
  padding-bottom: ${em(20)};

  ${p => p.theme.mq.upMd} {
    padding-top: ${p => math(`${p.theme.blocks.menuHeightMd} + ${em(54)}`)};
    padding-left: ${p => p.theme.indents.baseIndentMd};
    padding-right: ${p => p.theme.indents.baseIndentMd};
    padding-bottom: ${em(30)};
  }

  ${p => p.theme.mq.upLg} {
    font-size: 16px;
    max-width: ${em(1288)};
    margin: 0 auto;
    padding-top: ${p => math(`${p.theme.blocks.menuHeightMd} + ${em(80)}`)};
    padding-left: 0;
    padding-right: 0;
    padding-bottom: ${em(20)};
  }
`

export const HeaderRow = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${em(20)};

  ${p => p.theme.mq.upMd} {
    gap: ${em(12)};
  }

  ${p => p.theme.mq.upLg} {
    flex-direction: row;
    align-items: flex-start;
    gap: ${em(28)};
    width: ${em(763)};
    max-width: 100%;
  }
`

export const Title = styled.h1`
  margin: 0;
  width: 100%;
  max-width: ${em(320)};
  font-family: ${p => p.theme.typography.fontDruk};
  font-size: ${em(30)};
  line-height: ${em(30, 30)};
  text-align: center;

  ${p => p.theme.mq.upMd} {
    font-size: ${em(52)};
    line-height: 0.96;
  }

  ${p => p.theme.mq.upLg} {
    width: auto;
    max-width: none;
    font-size: ${em(40)};
    line-height: ${em(40, 40)};
    text-align: left;
  }
`

export const TitleBreak = styled.span`
  display: block;

  ${p => p.theme.mq.upLg} {
    display: inline;
    margin-left: 0.3em;
  }
`

export const HelperLink = styled.button.attrs({
  className: 'default-styles',
  type: 'button',
})`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: 0 !important;
  background: transparent !important;
  box-shadow: none;
  outline: none;
  appearance: none;
  -webkit-appearance: none;
  font-family: ${p => p.theme.typography.mediumFont};
  font-size: ${em(16)};
  line-height: ${em(20, 16)};
  color: ${CART_ACCENT_PURPLE};
  text-decoration: underline;
  text-decoration-color: currentColor;
  text-decoration-skip-ink: none;
  cursor: pointer;
  transition: opacity ${p => p.theme.animation.timeStandard} ease;

  &:hover {
    opacity: 0.5;
  }

  &::-moz-focus-inner {
    border: 0;
    padding: 0;
  }

  ${p => p.theme.mq.upLg} {
    margin-left: auto;
    margin-top: ${em(16)};
  }
`

export const MainGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${em(60)};
  padding-left: 0;
  padding-right: 0;

  ${p => p.theme.mq.upMd} {
    gap: ${em(60)};
    padding-left: ${p => p.theme.indents.baseIndentMd};
    padding-right: ${p => p.theme.indents.baseIndentMd};
  }

  ${p => p.theme.mq.upLg} {
    font-size: 16px;
    width: ${em(1288)};
    margin: 0 auto;
    padding-left: 0;
    padding-right: 0;
    display: grid;
    grid-template-columns: ${em(763)} ${em(436)};
    column-gap: ${em(89)};
    row-gap: 0;
    align-items: start;
  }
`

export const FormCard = styled.div`
  background: ${p => p.theme.colors.grayOp06NonTransp};
  border-radius: ${em(20)};
  padding: ${em(40)} ${em(20)};
  order: 2;

  ${p => p.theme.mq.upMd} {
    padding: ${em(40)};
  }

  ${p => p.theme.mq.upLg} {
    order: 1;
    min-height: ${em(996)};
  }

  form {
    width: 100%;
  }

  h4 {
    margin-bottom: 20px;
  }

  .separator {
    height: ${em(16)};
  }

  .separator-double {
    height: ${em(30)};

    ${p => p.theme.mq.upMd} {
      height: ${em(40)};
    }
  }

  .delivery-map-button {
    width: auto;
  }
`

export const SummaryColumn = styled.aside`
  order: 1;
  padding-left: ${p => p.theme.indents.baseIndentXs};
  padding-right: ${p => p.theme.indents.baseIndentXs};

  ${p => p.theme.mq.upLg} {
    padding-left: 0;
    padding-right: 0;
    width: ${em(436)};
    order: 2;
    position: sticky;
    top: ${p => math(`${p.theme.blocks.menuHeightMd} + ${em(40)}`)};
  }
`

export const SummaryTitleRow = styled.div`
  display: flex;
  align-items: baseline;
  gap: ${em(8)};
  margin-bottom: ${em(20)};
`

export const SummaryTitle = styled.h2`
  margin: 0;
  font-family: ${p => p.theme.typography.mediumFont};
  font-size: ${em(22)};
  line-height: ${em(30, 22)};
  font-weight: 400;

  ${p => p.theme.mq.upLg} {
    font-size: ${em(26)};
    line-height: ${em(36, 26)};
  }
`

export const SummaryCount = styled.span`
  color: ${p => p.theme.colors.blackOp4};
  font-size: ${em(14)};
  line-height: ${em(20, 14)};

  ${p => p.theme.mq.upLg} {
    font-size: ${em(16)};
  }
`

export const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: ${em(287)};
  gap: 0;
  margin: ${em(20)} auto 0;
  padding: 0;
  text-align: center;

  ${p => p.theme.mq.upLg} {
    margin-top: ${em(40)};
  }
`

export const EmptyIllustration = styled.div`
  position: relative;
  width: ${em(150)};
  height: ${em(150)};

  ${p => p.theme.mq.upLg} {
    width: ${em(170)};
    height: ${em(170)};
  }
`

export const EmptyIllustrationBase = styled.img`
  position: absolute;
  inset: 0;
  display: block;
  width: 100%;
  height: 100%;
  opacity: 0.59;
  filter: grayscale(1) brightness(1.04);
`

export const EmptyIllustrationEye = styled.img`
  position: absolute;
  top: ${em(75.88)};
  display: block;
  width: ${em(23.824)};
  height: ${em(23.824)};
  transform: scaleY(-1) rotate(180deg);

  ${p =>
    p.$side === 'left'
      ? css`
          left: ${em(30.88)};
        `
      : css`
          left: ${em(60.88)};
        `}

  ${p => p.theme.mq.upLg} {
    top: ${em(86)};
    width: ${em(27)};
    height: ${em(27)};

    ${p =>
      p.$side === 'left'
        ? css`
            left: ${em(35)};
          `
        : css`
            left: ${em(69)};
          `}
  }
`

export const EmptyIllustrationPupil = styled.img`
  position: absolute;
  display: block;
  width: ${em(9.233)};
  height: ${em(12.463)};
  transform: scaleY(-1) rotate(150deg);

  ${p =>
    p.$side === 'left'
      ? css`
          left: ${em(45.39)};
          top: ${em(77.36)};
        `
      : css`
          left: ${em(75.02)};
          top: ${em(77.03)};
        `}

  ${p => p.theme.mq.upLg} {
    width: ${em(10.464)};
    height: ${em(14.124)};

    ${p =>
      p.$side === 'left'
        ? css`
            left: ${em(51.45)};
            top: ${em(87.68)};
          `
        : css`
            left: ${em(85.02)};
            top: ${em(87.3)};
          `}
  }
`

export const EmptyIllustrationMouth = styled.img`
  position: absolute;
  left: ${em(53.67)};
  top: ${em(102.35)};
  display: block;
  width: ${em(9.788)};
  height: ${em(2.965)};
  transform: scaleY(-1) rotate(-1.89deg);

  ${p => p.theme.mq.upLg} {
    left: ${em(60.83)};
    top: ${em(116)};
    width: ${em(11.093)};
    height: ${em(3.361)};
  }
`

export const EmptyText = styled.p`
  margin: 0;
  max-width: 100%;
  color: ${p => p.theme.colors.black};
  opacity: 0.5;
  font-family: ${p => p.theme.typography.mediumFont};
  font-size: ${em(22)};
  line-height: ${em(30, 22)};
  white-space: nowrap;
`

export const SummaryItems = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${em(40)};

  ${p => p.theme.mq.upLg} {
    gap: 0;
  }
`

export const SummaryGroup = styled.div`
  position: relative;
`

export const SummarySeparator = styled.div`
  display: none;

  ${p => p.theme.mq.upLg} {
    display: block;
    height: 1px;
    margin: ${em(20)} 0 ${em(20)};
    background: rgba(0, 0, 0, 0.1);
  }
`

export const SummaryItem = styled.div`
  position: relative;
  min-height: ${em(90)};

  ${p => p.theme.mq.upMd} {
    display: flex;
    align-items: center;
    gap: ${em(20)};
    min-height: ${em(74)};
  }

  ${p => p.theme.mq.upLg} {
    display: block;
  }
`

export const SummaryImageFrame = styled.div`
  position: absolute;
  left: 0;
  top: ${em(6.93)};
  flex-shrink: 0;
  width: ${em(100)};
  height: ${em(66.667)};
  overflow: hidden;

  ${p => p.theme.mq.upMd} {
    position: relative;
    left: auto;
    top: auto;
    width: ${em(111)};
    height: ${em(74)};
  }

  ${p => p.theme.mq.upLg} {
    position: absolute;
    left: 0;
    top: 0;
  }
`

export const SummaryImage = styled.img`
  position: absolute;
  left: 0;
  top: 0;
  display: block;
  width: 100%;
  height: 100%;
  object-fit: contain;
  object-position: center center;

  ${p => p.theme.mq.upMd} {
    width: 100%;
    height: 100%;
  }
`

export const SummaryInfo = styled.div`
  position: absolute;
  left: ${em(110)};
  top: ${em(6)};
  width: ${em(196)};
  min-width: 0;
  max-width: ${em(196)};

  ${p => p.theme.mq.upMd} {
    position: static;
    flex: 1 1 auto;
    width: auto;
    max-width: none;
  }

  ${p => p.theme.mq.upLg} {
    position: absolute;
    left: ${em(131)};
    top: ${em(15)};
    width: ${em(196)};
    max-width: ${em(196)};
  }
`

export const SummaryItemTitle = styled.p`
  margin: 0 0 4px !important;
  font-size: ${em(16)};
  line-height: ${em(20, 16)};
`

export const SummaryPriceRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${em(5)};

  > span > div {
    margin: 0;
    font-family: ${p => p.theme.typography.mediumFont};
    font-size: ${em(16)};
    line-height: ${em(20, 16)};
    font-weight: 400;
  }
`

export const QuantityControl = styled.div`
  position: absolute;
  left: ${em(110)};
  top: ${em(60)};
  display: block;
  width: ${em(100)};
  height: ${em(30)};
  padding: 0;
  border-radius: ${em(10)};
  background: #f7f7f7;

  ${p => p.theme.mq.upMd} {
    position: relative;
    top: auto;
    left: auto;
    flex-shrink: 0;
    height: ${em(35)};
  }

  ${p => p.theme.mq.upLg} {
    position: absolute;
    left: ${em(336)};
    top: ${em(19.5)};
  }
`

export const QuantityButton = styled.button`
  position: absolute;
  top: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: ${p => (p.$kind === 'minus' ? 'flex-start' : 'flex-end')};
  width: ${em(20)};
  height: ${em(20)};
  padding: 0;
  border: 0;
  background: transparent;
  cursor: pointer;
  transform: translateY(-50%);
  transition: opacity ${p => p.theme.animation.timeStandard} ease;

  &:hover:not(:disabled) {
    opacity: 0.5;
  }

  ${p =>
    p.$kind === 'minus'
      ? css`
          left: ${em(15)};
        `
      : css`
          right: ${em(15)};
        `}

  ${p => p.theme.mq.upMd} {
    top: ${em(8)};
    transform: none;

    ${p =>
      p.$kind === 'minus'
        ? css`
            left: ${em(15.25)};
          `
        : css`
            right: ${em(15.25)};
          `}
  }

  &:disabled {
    opacity: 0.35;
    cursor: default;
  }
`

export const QuantitySign = styled.span`
  display: block;
  width: ${p => (p.$kind === 'plus' ? em(10.5) : em(10))};
  height: ${p => (p.$kind === 'plus' ? em(10.5) : em(1.5))};
  background: center / contain no-repeat
    url(${p =>
      p.$kind === 'plus'
        ? summaryCounterPlus
        : summaryCounterMinus});

  ${p => p.theme.mq.upMd} {
    width: ${p => (p.$kind === 'plus' ? em(11.5) : em(11.5))};
    height: ${p => (p.$kind === 'plus' ? em(11.5) : em(1.5))};
  }
`

export const QuantityValue = styled.span`
  position: absolute;
  left: 50%;
  top: 50%;
  min-width: ${em(12)};
  text-align: center;
  font-family: ${p => p.theme.typography.mediumFont};
  font-size: ${em(16)};
  line-height: ${em(20, 16)};
  transform: translate(-50%, -50%);

  ${p => p.theme.mq.upMd} {
    top: ${em(8)};
    transform: translateX(-50%);
  }
`

export const AddonCard = styled.div`
  position: relative;
  display: block;
  height: ${em(85)};
  margin-top: ${em(20)};
  padding: 0;
  border-radius: ${em(10)};
  background: #f5f5f5;

  ${p => p.theme.mq.upMd} {
    display: flex;
    align-items: center;
    gap: ${em(24)};
    height: ${em(70)};
    padding: 0 ${em(15)} 0 ${em(30)};
  }

  ${p => p.theme.mq.upLg} {
    display: block;
    padding: 0;
  }
`

export const AddonThumb = styled.div`
  position: absolute;
  left: ${em(19.116)};
  top: ${em(17)};
  flex-shrink: 0;
  width: ${em(50.769)};
  height: ${em(75)};

  ${p => p.theme.mq.upMd} {
    position: relative;
    left: auto;
    top: auto;
    width: ${em(50.625)};
    height: ${em(60)};
  }

  ${p => p.theme.mq.upLg} {
    position: absolute;
    left: ${em(29)};
    top: ${em(10)};
    width: ${em(50.625)};
    height: ${em(60)};
  }
`

export const AddonPhoneFrame = styled.img`
  display: none;
`

export const AddonConsultant = styled.img`
  position: absolute;
  left: 0;
  top: 50%;
  display: block;
  width: 100%;
  height: auto;
  transform: translateY(-50%);
`

export const AddonBody = styled.div`
  position: absolute;
  left: ${em(110)};
  top: ${em(10)};
  width: ${em(210)};
  min-width: 0;
  max-width: ${em(210)};

  ${p => p.theme.mq.upMd} {
    position: static;
    flex: 1 1 auto;
    width: auto;
    max-width: none;
  }

  ${p => p.theme.mq.upLg} {
    position: absolute;
    left: ${em(129)};
    top: ${em(15)};
    width: ${em(218)};
    max-width: ${em(218)};
  }
`

export const AddonTitle = styled.p`
  margin: 0 !important;
  font-size: ${em(16)};
  line-height: ${em(20, 16)};
`

export const AddonMeta = styled.div`
  display: flex;
  align-items: center;
  gap: ${em(10)};
  margin-top: ${em(5)};

  ${p => p.theme.mq.upMd} {
    margin-top: ${em(4)};
  }

  ${p => p.theme.mq.upLg} {
    margin-top: 0;
  }

  .typography__CartItemTitle-msjfoa-19 {
    margin: 0;
    font-family: ${p => p.theme.typography.mediumFont};
    font-size: ${em(16)};
    line-height: ${em(20, 16)};
  }
`

export const AddonDuration = styled.span`
  color: rgba(0, 0, 0, 0.4);
  font-size: ${em(16)};
  line-height: ${em(20, 16)};
`

export const AddonToggle = styled.button.attrs({
  className: 'default-styles',
})`
  position: absolute;
  right: ${em(10)};
  top: ${em(25)};
  flex-shrink: 0;
  width: ${em(50)};
  height: ${em(30)};
  padding: 0;
  border: 0 !important;
  border-radius: ${em(15)};
  background: ${p => (p.$isActive ? CART_ACCENT_PURPLE : '#e5e5e5')};
  background-color: ${p =>
    p.$isActive ? CART_ACCENT_PURPLE : '#e5e5e5'} !important;
  cursor: pointer;
  transition: background-color ${p => p.theme.animation.timeStandard};

  ${p => p.theme.mq.upMd} {
    position: relative;
    right: auto;
    top: auto;
  }

  ${p => p.theme.mq.upLg} {
    position: absolute;
    right: ${em(15)};
    top: ${em(20)};
  }

  &:disabled {
    cursor: default;
    opacity: 0.55;
  }
`

export const AddonToggleKnob = styled.span`
  position: absolute;
  top: ${em(5)};
  left: ${p => (p.$isActive ? `calc(100% - ${em(25)})` : em(5))};
  width: ${em(20)};
  height: ${em(20)};
  border-radius: 50%;
  background: ${p => p.theme.colors.white};
  transition: left ${p => p.theme.animation.timeStandard};
`

export const PromoCodeBox = styled.div`
  margin-top: ${em(60)};

  ${p => p.theme.mq.upMd} {
    margin-top: ${em(40)};
  }

  form {
    margin: 0;
  }
`

export const PromoTriggerButton = styled.button.attrs({
  className: 'default-styles',
})`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  height: ${em(50)};
  padding: 0 ${em(15)} 0 ${em(12)};
  border: 1px solid rgba(0, 0, 0, 0.15) !important;
  border-radius: ${em(10)};
  background: ${p => p.theme.colors.white};
  background-color: ${p => p.theme.colors.white} !important;
  cursor: pointer;

  ${p => p.theme.mq.upMd} {
    height: ${em(60)};
    padding: 0 ${em(14)} 0 ${em(15)};
  }
`

export const PromoTriggerLabel = styled.span`
  color: rgba(0, 0, 0, 0.4);
  font-size: ${em(16)};
  line-height: ${em(20, 16)};
`

export const PromoArrow = styled.span.attrs({
  className: 'promo-arrow',
})`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: ${em(20)};
  height: ${em(20)};
  flex-shrink: 0;
  background: center / contain no-repeat url(${promoArrowRight});
  cursor: pointer;
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 0.5;
  }
`

export const PromoForm = styled.form`
  position: relative;
`

export const PromoInputField = styled.label`
  position: relative;
  display: block;
  width: 100%;
`

export const PromoStateField = styled.button.attrs({
  className: 'default-styles',
})`
  position: relative;
  display: block;
  width: 100%;
  height: ${em(50)};
  padding: 0;
  border: 1px solid rgba(0, 0, 0, 0.15) !important;
  border-radius: ${em(10)};
  background: ${p => p.theme.colors.white};
  background-color: ${p => p.theme.colors.white} !important;
  text-align: left;

  ${p => p.theme.mq.upMd} {
    height: ${em(60)};
  }
`

export const PromoStateLabel = styled.span`
  position: absolute;
  left: ${em(12)};
  top: ${p => (p.$isFloating ? em(5) : '50%')};
  color: rgba(0, 0, 0, 0.4);
  font-size: ${em(14)};
  line-height: ${em(20, 14)};
  transform: ${p => (p.$isFloating ? 'none' : 'translateY(-50%)')};
  transition:
    top ${p => p.theme.animation.timeStandard} ${p => p.theme.animation.cubicBezier},
    transform ${p => p.theme.animation.timeStandard} ${p => p.theme.animation.cubicBezier};

  ${p => p.theme.mq.upMd} {
    left: ${em(14)};
    top: ${p => (p.$isFloating ? em(8) : '50%')};
  }
`

export const PromoStateValue = styled.span`
  position: absolute;
  left: ${em(12)};
  top: ${em(20)};
  max-width: calc(100% - ${em(90)});
  overflow: hidden;
  color: ${p => p.theme.colors.black};
  font-family: ${p => p.theme.typography.mediumFont};
  font-size: ${em(16)};
  line-height: ${em(20, 16)};
  text-overflow: ellipsis;
  white-space: nowrap;

  ${p => p.theme.mq.upMd} {
    left: ${em(14)};
    top: ${em(28)};
  }
`

export const PromoStateAction = styled.button.attrs({
  className: 'default-styles',
})`
  position: absolute;
  right: ${em(14)};
  top: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: 0 !important;
  background: transparent;
  color: ${p => p.theme.colors.black};
  font-size: ${em(16)};
  line-height: ${em(20, 16)};
  text-decoration: underline;
  text-decoration-skip-ink: none;
  transform: translateY(-50%);
  cursor: pointer;
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 0.5;
  }
`

export const PromoInput = styled.input`
  width: 100%;
  height: ${em(50)};
  padding: ${p =>
    p.$isFloating
      ? `${em(28)} ${em(48)} ${em(12)} ${em(12)}`
      : `${em(20)} ${em(48)} ${em(20)} ${em(12)}`};
  border: 1px solid rgba(0, 0, 0, 0.15);
  border-radius: ${em(10)};
  outline: none;
  background: ${p => p.theme.colors.white};
  font-family: ${p => p.theme.typography.mediumFont};
  font-size: ${em(16)};
  line-height: ${em(20, 16)};

  ${p => p.theme.mq.upMd} {
    height: ${em(60)};
    padding: ${p =>
      p.$isFloating
        ? `${em(28)} ${em(48)} ${em(12)} ${em(14)}`
        : `${em(20)} ${em(48)} ${em(20)} ${em(14)}`};
  }

  &::placeholder {
    color: rgba(0, 0, 0, 0.4);
  }
`

export const PromoApplyButton = styled.button.attrs({
  className: 'default-styles',
})`
  position: absolute;
  right: ${em(14)};
  top: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: ${em(20)};
  height: ${em(20)};
  padding: 0;
  border: 0 !important;
  background: transparent !important;
  color: ${p => p.theme.colors.black};
  transform: translateY(-50%);
  cursor: pointer;

  &:disabled {
    opacity: 0.55;
    cursor: default;
  }
`

export const PromoApplied = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  min-height: ${em(60)};
  padding: 0 ${em(15)};
  border: 1px solid rgba(0, 0, 0, 0.15);
  border-radius: ${em(10)};
  background: ${p => p.theme.colors.white};
`

export const PromoAppliedText = styled.span`
  color: #1d1d1f;
  font-size: ${em(16)};
  line-height: ${em(20, 16)};
`

export const PromoResetButton = styled.button`
  padding: 0;
  border: 0;
  background: transparent;
  color: rgba(0, 0, 0, 0.4);
  font-size: ${em(14)};
  line-height: ${em(20, 14)};
  cursor: pointer;
`

export const PromoFeedback = styled.p`
  margin: 0;
  padding-left: ${em(15)};
  color: ${p => {
    if (p.$isError) return '#E961A0'
    if (p.$isSuccess) return '#00DA67'
    return p.theme.colors.blackOp4
  }};
  font-size: ${em(14)};
  line-height: ${em(20, 14)};
`

export const DiscountRows = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${em(8)};
  margin-top: ${em(20)};
  min-height: ${em(56)};

  ${p => p.theme.mq.upMd} {
    min-height: 0;
  }
`

export const DiscountRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${em(16)};
`

export const DiscountLabel = styled.span`
  font-size: ${em(15)};
  line-height: ${em(20, 15)};

  ${p => p.theme.mq.upLg} {
    font-size: ${em(16)};
  }
`

export const DiscountValue = styled.div`
  color: ${p => p.theme.colors.pink};
`

export const TotalRow = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: ${em(16)};
  margin-top: ${p => (p.$hasDiscounts ? em(10) : em(20))};

  > span + span > div {
    margin: 0;
    font-family: ${p => p.theme.typography.mediumFont};
    font-size: ${em(22)};
    line-height: ${em(30, 22)};
    font-weight: 400;
  }

  ${p => p.theme.mq.upMd} {
    > span + span > div {
      font-size: ${em(26)};
      line-height: ${em(35, 26)};
    }
  }
`

export const TotalLabel = styled.span`
  font-family: ${p => p.theme.typography.mediumFont};
  font-size: ${em(16)};
  line-height: ${em(20, 16)};
`

export const CheckoutButton = styled.button.attrs({
  className: 'default-styles',
})`
  display: block;
  width: 100%;
  height: ${em(40)};
  margin-top: ${em(35)};
  padding: 0 ${em(30)};
  border: 0 !important;
  border-radius: ${em(100)};
  background: ${CART_ACCENT_PURPLE};
  background-color: ${CART_ACCENT_PURPLE} !important;
  color: ${p => p.theme.colors.white};
  font-size: ${em(16)};
  line-height: ${em(20, 16)};
  text-align: center;
  cursor: pointer;

  transition: color ${p => p.theme.animation.timeStandard} ease;

  ${p => p.theme.mq.upMd} {
    height: ${em(50)};
    margin-top: ${em(20)};
    padding: ${em(11)} ${em(30)} ${em(14)};
  }

  &:hover:not(:disabled) {
    color: rgba(255, 255, 255, 0.5);
  }

  &:disabled {
    opacity: 0.45;
    cursor: default;
  }
`

export const CheckoutOfferError = styled.div`
  ${p =>
    p.$visible
      ? css`
          margin-top: ${em(16)};
          color: ${p.theme.colors.pink};
          font-size: ${em(14)};
          line-height: 1.4;
          text-align: center;
          visibility: visible;
        `
      : css`
          display: none;
        `};

  ${p => p.theme.mq.upMd} {
    font-size: ${em(14)};
  }
`

export const Recommendations = styled.div`
  margin-top: ${em(30)};
  padding-left: ${p => p.theme.indents.baseIndentXs};
  padding-right: ${p => p.theme.indents.baseIndentXs};
  overflow-x: hidden;

  ${p => p.theme.mq.upMd} {
    margin-top: ${em(60)};
    padding-left: ${p => p.theme.indents.baseIndentMd};
    padding-right: ${p => p.theme.indents.baseIndentMd};
  }

  ${p => p.theme.mq.upLg} {
    font-size: 16px;
    max-width: ${em(1288)};
    margin-left: auto;
    margin-right: auto;
    padding-left: 0;
    padding-right: 0;
  }
`

export const RecommendationsRail = styled.div`
  display: flex;
  gap: ${em(10)};
  overflow-x: auto;
  padding-bottom: ${em(6)};
  padding-right: ${p => p.theme.indents.baseIndentXs};
  margin-right: -${p => p.theme.indents.baseIndentXs};
  box-sizing: content-box;

  ${p => p.theme.mq.upMd} {
    display: grid;
    grid-template-columns: repeat(2, ${em(292)});
    column-gap: ${em(20)};
    justify-content: space-between;
    row-gap: ${em(20)};
    overflow: visible;
    margin-right: 0;
    padding-bottom: 0;
    padding-right: 0;
  }

  ${p => p.theme.mq.upLg} {
    grid-template-columns: repeat(4, ${em(292)});
    column-gap: ${em(40)};
    justify-content: start;
    row-gap: 0;
  }
`

export const RecommendationsMobileSlider = styled.div`
  .swiper-container {
    overflow: visible;
    margin-right: -${p => p.theme.indents.baseIndentXs};
    padding-bottom: 0;
  }

  .swiper-wrapper,
  .swiper-slide {
    height: auto;
  }

  ${p => p.theme.mq.upMd} {
    .swiper-container {
      overflow: hidden;
      margin-right: 0;
    }
  }
`

export const RecommendationSlide = styled.div`
  width: ${em(210)};
`

export const RecommendationCard = styled.div`
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  min-width: ${em(210)};
  min-height: ${em(358)};
  padding: ${em(20)};
  border-radius: ${em(20)};
  background: ${p => p.theme.colors.grayOp06NonTransp};

  ${p => p.theme.mq.upMd} {
    position: relative;
    width: ${em(292)};
    height: ${em(445)};
    min-width: ${em(292)};
    min-height: ${em(445)};
    padding: 0;
    overflow: hidden;
  }
`

export const RecommendationImageWrap = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: ${em(140)};
  margin-bottom: ${em(10)};
  overflow: hidden;

  ${p => p.theme.mq.upMd} {
    position: absolute;
    left: ${em(20)};
    top: ${em(40)};
    width: ${em(252)};
    height: ${em(202)};
    margin-bottom: 0;
  }
`

export const RecommendationImage = styled.img`
  display: block;
  width: 100%;
  height: 100%;
  object-fit: contain;
  object-position: center center;

  ${p => p.theme.mq.upMd} {
    width: 100%;
    height: 100%;
  }
`

export const RecommendationTitle = styled.p`
  margin: 0 0 ${em(16)};
  font-family: ${p => p.theme.typography.baseFont};
  font-size: ${em(16)};
  line-height: ${em(25, 16)};
  font-weight: 400;

  ${p => p.theme.mq.upMd} {
    position: absolute;
    left: ${em(20)};
    top: ${em(252)};
    width: ${em(252)};
    margin: 0;
  }
`

export const RecommendationPricing = styled.div`
  margin-top: auto;

  ${p => p.theme.mq.upMd} {
    position: absolute;
    left: ${em(20)};
    top: ${em(295)};
    width: ${em(252)};
  }
`

export const RecommendationMetaRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${em(5)};
  min-height: ${em(25)};

  ${p => p.theme.mq.upMd} {
    height: ${p => (p.$reserveDiscountSpace ? em(25) : 0)};
    min-height: ${p => (p.$reserveDiscountSpace ? em(25) : 0)};
  }
`

export const RecommendationOldPrice = styled.div`
  display: flex;
  align-items: center;
  color: ${p => p.theme.colors.blackOp5};
  text-decoration: line-through;

  ${p => p.theme.mq.upMd} {
    height: ${em(25)};
  }

  > span {
    display: block;
    line-height: 0;
  }

  > span > div {
    margin: 0;
    font-family: ${p => p.theme.typography.mediumFont};
    font-size: ${em(16)};
    line-height: ${em(25, 16)};
    font-weight: 400;
  }
`

export const RecommendationPriceRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${em(5)};
  margin-top: ${em(4)};

  ${p => p.theme.mq.upMd} {
    margin-top: 0;
  }

  > span > div {
    margin: 0;
    font-family: ${p => p.theme.typography.boldFont};
    font-size: ${em(22)};
    line-height: ${em(30, 22)};
    font-weight: 400;
  }

  ${p => p.theme.mq.upMd} {
    > span > div {
      font-family: ${p => p.theme.typography.mediumFont};
      font-size: ${em(26)};
      line-height: ${em(35, 26)};
    }
  }
`

export const DiscountChip = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: ${em(39, 12)};
  height: ${em(21, 12)};
  padding: 0 ${em(8, 12)};
  border-radius: ${em(4, 12)};
  background: ${p => p.theme.colors.pink};
  color: ${p => p.theme.colors.white};
  font-family: ${p => p.theme.typography.boldFont};
  font-size: ${em(12)};
  line-height: ${em(16, 12)};
  font-weight: 400;

  ${p => p.theme.mq.upLg} {
    width: ${em(39, 12)};
    min-width: ${em(39, 12)};
    height: ${em(21, 12)};
    padding: 0;
  }
`

const recommendationActionCss = css`
  display: block;
  width: 100%;
  height: ${em(40)};
  margin-top: ${em(20)};
  border: 0;
  border-radius: ${em(1000)};
  background-color: ${p => p.theme.colors.white} !important;
  box-shadow: 0 ${em(3)} ${em(12)} rgba(0, 0, 0, 0.05);

  ${p => p.theme.mq.upMd} {
    height: ${em(50)};
  }
`

export const RecommendationButton = styled.button`
  ${recommendationActionCss};
  padding: 0 ${em(30)};
  cursor: pointer;

  &:hover .recommendation-button-label {
    color: rgba(0, 0, 0, 0.5);
  }

  ${p => p.theme.mq.upMd} {
    position: absolute;
    left: ${em(20)};
    bottom: ${em(20)};
    width: ${em(252)};
    margin-top: 0;
  }
`

export const RecommendationButtonLabel = styled.span.attrs({
  className: 'recommendation-button-label',
})`
  display: block;
  font-family: ${p => p.theme.typography.baseFont};
  font-size: ${em(16)};
  line-height: ${em(25, 16)};
  font-weight: 400;
  color: ${p => p.theme.colors.black};
  transition: color ${p => p.theme.animation.timeStandard} ease;
`

export const RecommendationCounter = styled.div`
  ${recommendationActionCss};
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 ${em(20)};

  ${p => p.theme.mq.upMd} {
    position: absolute;
    left: ${em(20)};
    bottom: ${em(20)};
    width: ${em(252)};
    margin-top: 0;
    padding: 0;
  }
`

export const RecommendationCounterButton = styled.button`
  position: relative;
  width: ${em(24)};
  height: ${em(24)};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: 0;
  background: transparent;
  cursor: pointer;
  color: ${p => p.theme.colors.black};
  transition: opacity ${p => p.theme.animation.timeStandard} ease;

  &:hover:not(:disabled) {
    opacity: 0.5;
  }

  &:disabled {
    opacity: 0.35;
    cursor: default;
  }

  ${p => p.theme.mq.upMd} {
    position: absolute;
    top: 19.25px;
    width: ${p => (p.$kind === 'minus' ? '12.5px' : '11.5px')};
    height: 11.5px;
    ${p =>
      p.$kind === 'minus' &&
      css`
      left: 19.5px;
    `}
    ${p =>
      p.$kind === 'plus' &&
      css`
      left: 222px;
    `}
  }
`

export const RecommendationCounterSign = styled.span`
  display: block;
  width: 11.5px;
  height: ${p => (p.$kind === 'plus' ? '11.5px' : '1.5px')};
  background: center / contain no-repeat
    url(${p =>
      p.$kind === 'plus'
        ? recommendationCounterPlus
        : recommendationCounterMinus});
`

export const RecommendationCounterValue = styled.span`
  display: block;
  font-family: ${p => p.theme.typography.mediumFont};
  font-size: ${em(16)};
  line-height: ${em(25, 16)};
  font-weight: 400;
  color: ${p => p.theme.colors.black};

  ${p => p.theme.mq.upMd} {
    position: absolute;
    left: 50%;
    top: ${em(11)};
    transform: translateX(-50%);
  }
`

export const DeliveryBannerCard = styled.div`
  box-sizing: border-box;
  display: none;
  position: relative;
  min-width: ${em(252)};
  min-height: ${em(404)};
  padding: 0;
  overflow: hidden;
  border-radius: ${em(20)};
  background: #e89d9a;

  ${p => p.theme.mq.upMd} {
    display: block;
    width: ${em(292)};
    height: ${em(445)};
    min-width: ${em(292)};
    min-height: ${em(445)};
  }
`

export const DeliveryBannerCardMobile = styled(DeliveryBannerCard)`
  display: block;
  min-width: ${em(210)};
  min-height: ${em(358)};

  ${p => p.theme.mq.upMd} {
    display: none;
  }
`

export const DeliveryBannerTitleBox = styled.div`
  position: absolute;
  left: 50%;
  top: ${em(32)};
  z-index: 1;
  width: ${em(192)};
  transform: translateX(-50%);

  ${p => p.theme.mq.upMd} {
    top: ${em(40)};
    width: ${em(274)};
  }
`

export const DeliveryBannerTitle = styled.p`
  margin: 0;
  width: 100%;
  color: ${p => p.theme.colors.white};
  font-family: ${p => p.theme.typography.fontDruk};
  font-weight: 500;
  font-size: ${em(18)};
  line-height: ${em(18, 18)};
  text-align: center;
  letter-spacing: 0;
  font-synthesis: none;

  ${p => p.theme.mq.upMd} {
    font-size: ${em(22)};
    line-height: ${em(22, 22)};
  }
`

export const DeliveryBannerAccent = styled.span`
  display: block;
  width: fit-content;
  margin-left: auto;
  margin-right: auto;
  white-space: nowrap;
  color: #eafc7a;
`

export const DeliveryBannerTitleLine = styled.span`
  display: block;
  width: fit-content;
  margin-left: auto;
  margin-right: auto;
  white-space: nowrap;
`

export const DeliveryBannerArt = styled.div`
  position: absolute;
  top: 41.24%;
  right: -19.86%;
  bottom: 5.06%;
  left: -20.21%;
  overflow: hidden;
`

export const DeliveryBannerImage = styled.img`
  position: absolute;
  left: 0;
  top: -60.89%;
  display: block;
  width: 100%;
  height: 161.04%;
  max-width: none;
`

export const FaqSection = styled.section`
  margin-top: ${em(60)};
  padding-left: ${p => p.theme.indents.baseIndentXs};
  padding-right: ${p => p.theme.indents.baseIndentXs};

  ${p => p.theme.mq.upMd} {
    padding-left: ${p => p.theme.indents.baseIndentMd};
    padding-right: ${p => p.theme.indents.baseIndentMd};
  }

  ${p => p.theme.mq.upLg} {
    font-size: 16px;
    max-width: ${em(1070)};
    margin-left: auto;
    margin-right: auto;
    padding-left: 0;
    padding-right: 0;
    margin-top: ${em(72)};
  }
`

export const FaqHeading = styled.h2`
  margin: 0 0 ${em(20, 30)};
  font-family: ${p => p.theme.typography.fontDruk};
  font-feature-settings: 'liga' off, 'clig' off;
  font-size: ${em(30)};
  line-height: ${em(32, 30)};
  text-align: center;

  ${p => p.theme.mq.upMd} {
    font-size: ${em(40)};
    line-height: ${em(40, 40)};
    margin-bottom: ${em(30, 40)};
  }
`

export const FaqList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${em(10)};
`

export const FaqItem = styled.div`
  border-radius: ${em(20)};
  background: #f5f5f7;
  padding: ${em(15)};

  ${p => p.theme.mq.upMd} {
    background: #f7f7f7;
    padding: ${em(28)} ${em(30)} ${em(30)};
  }

  ${p =>
    p.isOpened &&
    css`
      padding-bottom: ${em(20)};

      ${p => p.theme.mq.upMd} {
        padding-bottom: ${em(30)};
      }
    `}
`

export const FaqQuestionRow = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 0;
  border: 0;
  background: transparent;
  color: inherit;
  cursor: pointer;
  text-align: left;

  &:hover .faq-toggle {
    opacity: 0.5;
  }
`

export const FaqQuestion = styled.span`
  padding-right: ${em(20)};
  font-family: ${p => p.theme.typography.mediumFont};
  font-size: ${em(17)};
  line-height: ${em(24, 17)};

  ${p => p.theme.mq.upLg} {
    font-size: ${em(26)};
    line-height: ${em(35, 26)};
  }
`

export const FaqToggle = styled.span.attrs({
  className: 'faq-toggle',
})`
  flex-shrink: 0;
  width: ${em(20)};
  height: ${em(20)};
  background: center / contain no-repeat
    url(${p => (p.$isOpened ? faqToggleMinus : faqTogglePlus)});
  transition: opacity ${p => p.theme.animation.timeStandard} ease;
`

export const FaqAnswer = styled.div`
  max-width: ${em(760)};
  margin-top: ${em(16)};
  color: rgba(0, 0, 0, 0.4);
  font-size: ${em(14)};
  line-height: ${em(20, 14)};
  padding-right: ${em(40)};

  > p {
    margin: 0;
  }

  > p + p {
    margin-top: ${em(16)};
  }

  a {
    color: inherit;
    text-decoration: underline;
  }

  ${p => p.theme.mq.upMd} {
    color: #1d1d1f;
    font-size: ${em(16)};
    line-height: ${em(25, 16)};
    padding-right: 0;
  }

  ${p => p.theme.mq.upLg} {
    width: 100%;
    max-width: 950px;
  }
`

export const DisclaimerSection = styled.div`
  margin-top: ${em(60)};
  padding-bottom: ${em(40)};
  padding-left: ${p => p.theme.indents.baseIndentXs};
  padding-right: ${p => p.theme.indents.baseIndentXs};

  ${p => p.theme.mq.upMd} {
    margin-top: ${em(60)};
    padding-bottom: ${em(60)};
    padding-left: ${p => p.theme.indents.baseIndentMd};
    padding-right: ${p => p.theme.indents.baseIndentMd};
  }

  ${p => p.theme.mq.upLg} {
    font-size: 16px;
    max-width: ${em(1070)};
    margin-left: auto;
    margin-right: auto;
    padding-left: 0;
    padding-right: 0;
  }
`
